// DAS-3157: Alpha register — Account -> "Alpha".
// Which signals beat the market (and by how much per EUR 10,000), how solid the
// evidence is, where each one is used in the app today, and — collapsed below —
// the research that found nothing, so a dead idea is visible before anyone
// re-researches it. Rows come from the `Alpha_Register` table via
// GET /api/alpha_register; every row links to its DAS ticket(s).
import React, { useEffect, useState } from 'react';
import { Modal, Table, Tag, Collapse, Spin, Empty, Typography } from 'antd';
import api from '../functions/api';

const { Text } = Typography;

const CATEGORY_COLOR = {
  found: 'green',
  small: 'gold',
  on_paper: 'orange',
  not_proven: 'default',
  no_alpha: 'red',
};

const ticketLinks = (row) => (
  <span>
    {(row.ticket_links || []).map((t) => (
      <a key={t.key} href={t.url} target="_blank" rel="noopener noreferrer" style={{ marginRight: 6 }}>
        {t.key}
      </a>
    ))}
  </span>
);

const columns = [
  {
    title: 'Signal',
    dataIndex: 'name',
    key: 'name',
    width: 220,
    render: (v, row) => (
      <div>
        <Text strong>{v}</Text>
        <div><Tag color={CATEGORY_COLOR[row.category] || 'default'}>{row.category_label}</Tag></div>
      </div>
    ),
  },
  {
    title: 'Result',
    dataIndex: 'result_text',
    key: 'result_text',
    render: (v, row) => (
      <div>
        <div>{v}</div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {[row.horizon && `horizon ${row.horizon}`, row.compared_with && `vs ${row.compared_with}`, row.period_tested]
            .filter(Boolean).join(' · ')}
        </Text>
      </div>
    ),
  },
  {
    title: 'How solid',
    dataIndex: 'solidity_notes',
    key: 'solidity_notes',
    width: 260,
    render: (v) => <span style={{ fontSize: 12 }}>{v}</span>,
  },
  {
    title: 'Used in the app today',
    dataIndex: 'used_in',
    key: 'used_in',
    width: 260,
    render: (v) => <span style={{ fontSize: 12 }}>{v || <Text type="secondary">not used yet</Text>}</span>,
  },
  {
    title: 'Tickets',
    key: 'tickets',
    width: 120,
    render: (_, row) => (
      <div>
        {ticketLinks(row)}
        {row.recheck_date && (
          <div><Text type="secondary" style={{ fontSize: 11 }}>re-check {row.recheck_date}</Text></div>
        )}
      </div>
    ),
  },
];

const AlphaRegisterModal = ({ isVisible, onClose }) => {
  const [data, setData] = useState({ usable: [], no_alpha: [], count: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isVisible) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get('/alpha_register')
      .then((res) => { if (!cancelled) setData(res.data || { usable: [], no_alpha: [], count: 0 }); })
      .catch((e) => {
        console.error('alpha register fetch failed', e);
        if (!cancelled) setError(e?.response?.data?.error || e.message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isVisible]);

  return (
    <Modal
      title={<span data-testid="alpha-register-title">Alpha — what beats the market, and how it is used</span>}
      open={isVisible}
      onCancel={onClose}
      footer={null}
      width={1180}
    >
      <div data-testid="alpha-register-modal">
        <Text type="secondary">
          Alpha = return above what the comparison would have earned over the same period. Amounts are per
          €10,000 invested. A row is only &quot;Found&quot; when it held in both halves of the period and a random
          pick scored about 0.
        </Text>
        {loading && <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>}
        {error && <div style={{ color: '#cf1322', marginTop: 12 }}>Could not load the register: {error}</div>}
        {!loading && !error && (
          <>
            <Table
              style={{ marginTop: 12 }}
              size="small"
              rowKey="id"
              pagination={false}
              columns={columns}
              dataSource={data.usable}
              locale={{ emptyText: <Empty description="No usable alpha registered yet" /> }}
              data-testid="alpha-register-usable"
            />
            <Collapse
              style={{ marginTop: 16 }}
              items={[{
                key: 'no_alpha',
                label: (
                  <span data-testid="alpha-register-no-alpha">
                    Research that found no alpha ({data.no_alpha.length})
                  </span>
                ),
                children: (
                  <Table
                    size="small"
                    rowKey="id"
                    pagination={false}
                    columns={columns}
                    dataSource={data.no_alpha}
                    locale={{ emptyText: <Empty description="Nothing registered" /> }}
                  />
                ),
              }]}
            />
          </>
        )}
      </div>
    </Modal>
  );
};

export default AlphaRegisterModal;
